<template>
    <div class="steps">
        <slot v-bind="defaultSlotProps"/>
    </div>
</template>

<script lang="ts">
    import {scrollToElement} from '@/library/dom';
    import {queryFocusableElements} from '@/library/focus';
    import {getVueChildren, identifyElement, isEmptyObject} from '@/library/utils';
    import {type ComponentPublicInstance} from 'vue';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import TopMessageHandler from '../../library/topMessageHandler';
    import TabBarContent from '../TabBarContent.vue';
    import TabBarLabel from '../TabBarLabel.vue';
    import FormRow from './FormRow.vue';

    export type FormStepIntention = Nullable<'next' | 'previous'>;

    type FormInputElement = ComponentPublicInstance & { isFormField: boolean; updateValue: () => Promise<any> };

    @Component({
        name: 'FormSteps',
        emits: ['mounted', 'changed-tab', 'form-step:validated'],
    })
    export default class FormSteps extends Vue
    {
        @Prop({
            type: String,
            required: true,
        }) tabBarGroup: string;

        @Prop({
            type: Boolean,
            required: true,
        }) useSteps: boolean;

        @Prop({
            type: Boolean,
            required: false,
            default: false,
        }) stepAsComponent: boolean;

        @Prop({
            type: Boolean,
            default: false,
        }) disableAutoFocus: boolean;

        intention: FormStepIntention = null;

        current: number = -1;

        currentMaxStep: number = 0;

        labels: Nullable<HTMLCollectionOf<HTMLDivElement>> = null;

        contents: Nullable<HTMLCollectionOf<HTMLElement>> = null;

        formIsComplete: boolean = false;

        forceFormIncomplete: boolean = false;

        completionTimeout: number = 0;

        get defaultSlotProps()
        {
            return {
                isFirst: this.first,
                isLast: this.last,
                nextStep: this.next,
                previousStep: this.previous,
                activeTab: this.activeTab,
                labels: this.labels,
                isComplete: this.formIsComplete && !this.forceFormIncomplete,
                checkCompletion: this.checkFormCompletion,
                setStep: this.setStep,
                saveCurrentStep: this.saveStep,
                setFormIncomplete: this.setForceFormIncomplete,
            };
        }

        get options(): TabBarData
        {
            return this.$store.state.tabBar[this.tabBarGroup] || {};
        }

        get activeTab()
        {
            return this.options.active;
        }

        get first(): boolean
        {
            return this.current === 0;
        }

        last = false;

        getLast(): boolean
        {
            if (!this.labels)
            {
                this.last = false;
                return;
            }

            this.last = this.labels.length - 1 === this.current;
        }

        get requiredFilters()
        {
            const filters = this.$store.state.form.filter;

            let requiredFilters: string[] = [];

            for (let filterKey in filters)
            {
                if (!filters.hasOwnProperty(filterKey) || !filters[filterKey].validators)
                {
                    continue;
                }

                for (const validator of filters[filterKey].validators)
                {
                    if (validator.name === 'NotEmpty')
                    {
                        requiredFilters.push(filterKey);
                        break;
                    }
                }
            }

            return requiredFilters;
        }

        @Watch('labels', {deep: true})
        @Watch('current')
        watchLabels()
        {
            this.getLast();
        }

        fetchElements()
        {
            this.labels   = this.$el.getElementsByClassName('tab-bar-label-' + this.tabBarGroup) as HTMLCollectionOf<HTMLDivElement>;
            this.contents = this.$el.getElementsByClassName('tab-bar-content-' + this.tabBarGroup) as HTMLCollectionOf<HTMLDivElement>;

            this.$forceUpdate();

            window.setTimeout(() => this.watchLabels(), 0);
        }

        mounted()
        {
            this.fetchElements();

            if (!this.labels!.length)
            {
                return;
            }

            const updateActiveTab = () =>
            {
                this.current = -1;

                for (let i = 0; i < this.labels!.length; i++)
                {
                    const label = this.getElements(i).label;

                    if (label && this.activeTab === label.name)
                    {
                        this.current = i;
                        break;
                    }
                }

                if (this.current === -1)
                {
                    this.setStep(0, true); // default to first tab if current tab is invalid (e.g. coming from the url parameter)
                }
                else if (this.current > this.currentMaxStep)
                {
                    this.setStep(this.currentMaxStep, true);
                }
            };

            // independent tab start e.g. via param
            if (!this.useSteps)
            {
                this.currentMaxStep = this.labels.length;
            }

            this.$watch('activeTab', updateActiveTab);
            updateActiveTab();

            this.$emit('mounted');
            this.emitter.emit('mounted');
        }

        scrollToElement()
        {
            scrollToElement(this.$el as HTMLElement);
        }

        previous()
        {
            if (this.labels && !this.first)
            {
                this.intention = 'previous';

                this.setStep(this.current - 1, !this.stepAsComponent);
            }
        }

        next()
        {
            if (!this.labels)
            {
                return;
            }

            if (!this.last)
            {
                this.intention = 'next';

                this.setStep(this.current + 1);
            }
            else if (this.stepAsComponent)
            {
                this.intention = 'next';

                this.setStep(this.current);
            }
        }

        setStep(index: number, ignoreValidation = false, force = false)
        {
            if (index > this.currentMaxStep + 1 && !force)
            {
                return;
            }

            if (index > this.currentMaxStep)
            {
                this.currentMaxStep = index;
            }

            const elements = this.getElements(index);
            const label    = elements.label!;

            if (ignoreValidation)
            {
                label.activateTab();
                this.scrollToElement();
                this.$nextTick(() =>
                {
                    !this.disableAutoFocus && elements.content && queryFocusableElements(elements.content.$el as HTMLElement).item(0)?.focus();
                });

                this.intention = null;
            }
            else
            {
                label.changeTab();
            }

            const data: FormStepsChangedTabData = {
                name: label.name,
                index,
            };

            if (force)
            {
                // set previous steps as valid
                for (let step = 0; step < index; step++)
                {
                    let label = this.getElements(step).label;

                    if (!label)
                    {
                        continue;
                    }

                    label.validated = true;
                    label.isValid   = 1;
                }
            }

            this.$emit('changed-tab', data);
            this.emitter.emit('changed-tab', data);
        }

        saveStep()
        {
            const elements = this.getElements(this.current);

            return elements.label!.validateAndSetStep(this.current);
        }

        validateContent(index: number, revalidate: boolean = true): Promise<void>
        {
            const {
                      content,
                      label,
                  } = this.getElements(index);

            if (!content || !label)
            {
                console.error('invalid index', index);
                return Promise.reject();
            }

            if (this.forceFormIncomplete)
            {
                return Promise.reject();
            }

            let promises: Promise<void>[] = [];

            for (const element of content.$el.querySelectorAll('.form-element.validate'))
            {
                const formRow = element.closest('.form-row')?.__vue__ as ComponentPublicInstance;

                for (const formElement of this.searchFormElements(formRow))
                {
                    promises.push(revalidate ? formElement.updateValue() : Promise.resolve());
                }
            }

            return Promise.all(promises).then(() =>
            {
                let errors = content.$el.getElementsByClassName('form-error') as HTMLCollectionOf<HTMLElement>;
                let state  = errors.length === 0 ? 1 : 0;

                // don't set validated state for content which can't be validated (no content to validate)
                if (content.$el.children.length)
                {
                    label.emitter.emit('form-step:validated', state);
                }

                if (errors.length)
                {
                    revalidate && TopMessageHandler.error('Es wurden fehlerhafte Eingaben festgestellt, bitte überprüfen Sie diese.');
                    // scroll to first error
                    scrollToElement(errors[0]);

                    return Promise.reject(errors);
                }

                return Promise.resolve();
            });
        }

        searchFormElements(component: ComponentPublicInstance | FormInputElement)
        {
            if (!component)
            {
                return [];
            }

            let formElements: FormInputElement[] = [];

            for (const child of getVueChildren(identifyElement(component)))
            {
                if (
                    // @ts-ignore
                    typeof child.isFormField !== 'undefined' && child.isFormField
                    // @ts-ignore
                    && typeof child.updateValue === 'function'
                )
                {
                    formElements.push(<FormInputElement>child);
                }

                formElements = [...formElements, ...this.searchFormElements(child)];
            }

            return formElements;
        }

        searchFormElementsRecursive(children: HTMLCollection, formElements: FormInputElement[])
        {
            for (const child of children)
            {
                if (
                    child.__vue__
                    // @ts-ignore
                    && child.__vue__.isFormField
                    // @ts-ignore
                    && typeof child.__vue__.updateValue === 'function'
                )
                {
                    formElements.push(<FormInputElement>child.__vue__);
                }

                this.searchFormElementsRecursive(child.children as HTMLCollection, formElements);
            }
        }

        validateCurrent(): Promise<void>
        {
            return this.validateContent(this.current);
        }

        async openInvalidTab()
        {
            if (!this.labels || !this.labels.length)
            {
                return;
            }

            let openTab = -1;

            await this.$nextTick();
            await new Promise(resolve => window.setTimeout(resolve, 100));

            for (let i = 0; i < this.labels.length; i++)
            {
                try
                {
                    await this.validateContent(i, false);
                }
                catch (e)
                {
                    // first or currently active
                    if (openTab === -1 || !this.useSteps && i === this.current)
                    {
                        openTab = i;
                    }
                }
            }

            if (openTab >= 0)
            {
                this.setStep(openTab, true);
            }
        }

        checkFormCompletion()
        {
            if (this.completionTimeout)
            {
                window.clearTimeout(this.completionTimeout);
            }

            this.$nextTick(() =>
            {
                this.completionTimeout = window.setTimeout(() =>
                {
                    for (const element of this.$el.querySelectorAll('.form-element.validate'))
                    {
                        const formRow = element.closest('.form-row')?.__vue__ as FormRow;

                        if (!formRow)
                        {
                            continue;
                        }

                        // only handle errors of existent elements
                        if (formRow.isInvalid)
                        {
                            this.formIsComplete = false;
                            return;
                        }

                        for (const child of getVueChildren(identifyElement(formRow)))
                        {
                            // @ts-ignore
                            const formElement = typeof child.getFormElement === 'function' ? child.getFormElement() : child;

                            // @ts-ignore
                            if (formElement.isFormField && (!formElement.modelValue || typeof formElement.modelValue === 'object' && isEmptyObject(formElement.modelValue)))
                            {
                                // special case for form-select with single-value "0"
                                if (formElement.$options.name === 'FormSelect' && !formElement.multiple && formElement.selectedOption !== null)
                                {
                                    continue;
                                }

                                // special case for provided files
                                if (formElement.$options.name === 'FormInputFile' && formElement.hasFiles)
                                {
                                    continue;
                                }

                                // @ts-ignore:2339 (filterName)
                                let errorName  = formElement.errorName || formRow._errorName;
                                // @ts-ignore:2339 (filterName)
                                let filterName = formElement.filterName || formRow._filterName || errorName;

                                // consider checking formRow.required directly
                                if (this.requiredFilters.includes(filterName))
                                {
                                    this.formIsComplete = false;
                                    return;
                                }
                            }
                        }
                    }

                    this.formIsComplete = true;
                }, 1000);
            });
        }

        setForceFormIncomplete(value: boolean)
        {
            this.forceFormIncomplete = value;
        }

        getElements(index: number)
        {
            return {
                label: this.labels ? this.labels[index]?.__vue__ as TabBarLabel : null,
                content: this.contents ? this.contents[index]?.__vue__ as TabBarContent : null,
            };
        }
    }
</script>